import { useSelector } from 'react-redux';
import { RootState } from '../../../support/store';
import React, { useMemo, useState } from 'react';
import { HbrCard, HbrIcon, HbrTag } from '@harbor/elements-react-standalone';
import {
  statusOk,
  statusGeneralDormant,
  statusInProgress,
  statusNegative,
} from '@harbor/elements/icons';
import JourneyMapBoard from '../../Common/JourneyMap/JourneyMapBoard';
import JourneyMapProgress from '../../Common/JourneyMap/JourneyMapProgress';
import JourneyMapItemModal from './JourneyMapItemModal';
import StbModal from '../../security-toolbox-ui/StbModal';
import '../../Common//JourneyMap/JourneyMapBoard.scss';

type ViewerSectionDelimiter = {
  id?: string | number;
  tempId?: string;
  x: number;
  y: number;
  orientation: 'horizontal' | 'vertical';
  style?: string;
  lineStyle?: 'solid' | 'dashed' | 'dotted';
  color?: string;
  label?: string;
  width?: number;
  isDeleted?: boolean;
};

type Props = {
  journeyMap?: any;
  items?: any[];
  links?: any[];
  assignmentId?: string | number | null;
  completionByItemId?: Record<string | number, boolean>;
  completionReasonByItemId?: Record<string | number, string>;
  pendingValidationByItemId?: Record<string | number, boolean>;
  syncStatusByItemId?: Record<string | number, string | null>;
  statusReasonByItemId?: Record<string | number, string>;
  notApplicableByItemId?: Record<string | number, boolean>;
  lockedByItemId?: Record<string | number, boolean>;
  isAssigned?: boolean;
  currentUserId?: string | number | null;
  onLaunchItem?: (item: any) => Promise<void> | void;
  onVideoCompleted?: (item: any) => void;
  onLaunchCourseItem?: (item: any) => void;
  onCompleteItem?: (item: any) => Promise<void> | void;
  onTaskSubmissionSaved?: (item: any, submission: any) => Promise<void> | void;
  onRequestAssignment?: () => Promise<void>;
  renderCourseItem?: (
    item: any,
    opts: {
      alreadyCompleted: boolean;
      completionReason?: string;
      onComplete: () => Promise<void> | void;
      onLaunchLearning?: () => void;
    }
  ) => React.ReactNode;
  assignmentPromptTitle?: string;
  assignmentPromptMessage?: string;
};

const DEFAULT_CANVAS_WIDTH = 1600;
const DEFAULT_CANVAS_HEIGHT = 900;

const isOverdue = (item: any, completed: boolean) => {
  if (completed) return false;
  const due = item?.available_to || item?.due_date || null;
  if (!due) return false;
  const ts = Date.parse(String(due));
  return !Number.isNaN(ts) && ts < Date.now();
};

const parseMetadata = (metadata: any) => {
  if (!metadata) return {};
  if (typeof metadata === 'string') {
    try {
      return JSON.parse(metadata);
    } catch {
      return {};
    }
  }
  return typeof metadata === 'object' ? metadata : {};
};

const toNumber = (value: any, fallback: number) => {
  const n = Number(value);
  return Number.isFinite(n) ? n : fallback;
};

const JourneyMapViewer: React.FC<Props> = ({
                                             journeyMap,
                                             items = [],
                                             links = [],
                                             assignmentId = null,
                                             completionByItemId = {},
                                             completionReasonByItemId = {},
                                             pendingValidationByItemId = {},
                                             syncStatusByItemId = {},
                                             statusReasonByItemId = {},
                                             notApplicableByItemId = {},
                                             lockedByItemId = {},
                                             isAssigned = false,
                                             currentUserId = null,
                                             onCompleteItem = async () => {},
                                             onTaskSubmissionSaved = async () => {},
                                             onRequestAssignment = async () => {},
                                             renderCourseItem,
                                             onLaunchItem,
                                             onVideoCompleted,
                                             onLaunchCourseItem,
                                             assignmentPromptTitle = 'Start this journey?',
                                             assignmentPromptMessage = 'You must be assigned to this journey before continuing. Confirming will create the assignment and open the selected step.',
                                           }) => {
  const currentUserIdFromStore = useSelector((state: RootState) => state.user?.userData?.id);
  const resolvedCurrentUserId = currentUserId ?? currentUserIdFromStore;

  const orderedItems = useMemo(() => {
    return [...items].sort((a, b) => (a.order || 0) - (b.order || 0));
  }, [items]);

  const viewerCompletionByItemId = useMemo(() => {
    const map: Record<string, boolean> = {};
    orderedItems.forEach((item) => {
      map[String(item.id)] = !!completionByItemId[String(item.id)];
    });
    return map;
  }, [orderedItems, completionByItemId]);

  const viewerPendingValidationByItemId = useMemo(() => {
    const map: Record<string, boolean> = {};
    orderedItems.forEach((item) => {
      map[String(item.id)] = !!pendingValidationByItemId[String(item.id)];
    });
    return map;
  }, [orderedItems, pendingValidationByItemId]);

  const viewerNotApplicableByItemId = useMemo(() => {
    const map: Record<string, boolean> = {};
    orderedItems.forEach((item) => {
      map[String(item.id)] = !!notApplicableByItemId[String(item.id)];
    });
    return map;
  }, [orderedItems, notApplicableByItemId]);

  const viewerLockedByItemId = useMemo(() => {
    const map: Record<string, boolean> = {};
    orderedItems.forEach((item) => {
      map[String(item.id)] = !!lockedByItemId[String(item.id)];
    });
    return map;
  }, [orderedItems, lockedByItemId]);

  const viewerSyncStatusByItemId = useMemo(() => {
    const map: Record<string, string | null> = {};
    orderedItems.forEach((item) => {
      const itemId = String(item.id);
      map[itemId] = syncStatusByItemId[itemId] ?? item?.sync_status ?? item?.progress?.sync_status ?? null;
    });
    return map;
  }, [orderedItems, syncStatusByItemId]);

  const [selectedItemId, setSelectedItemId] = useState<string | number | null>(null);
  const [pendingItemId, setPendingItemId] = useState<string | number | null>(null);
  const [assignmentModalOpen, setAssignmentModalOpen] = useState(false);
  const [assignmentError, setAssignmentError] = useState<string | null>(null);

  const selectedItem = useMemo(() => {
    if (selectedItemId == null) return null;
    return orderedItems.find((item) => String(item.id) === String(selectedItemId)) || null;
  }, [orderedItems, selectedItemId]);

  const pendingItem = useMemo(() => {
    if (pendingItemId == null) return null;
    return orderedItems.find((item) => String(item.id) === String(pendingItemId)) || null;
  }, [orderedItems, pendingItemId]);

  const overdueByItemId = useMemo(() => {
    return Object.fromEntries(
      orderedItems.map((item) => {
        const completed = !!viewerCompletionByItemId[String(item.id)];
        return [String(item.id), isOverdue(item, completed)];
      }),
    ) as Record<string, boolean>;
  }, [orderedItems, viewerCompletionByItemId]);

  const trackableItems = useMemo(() => {
    return orderedItems.filter((item) => item?.item_type !== 'section');
  }, [orderedItems]);

  const metadata = useMemo(() => parseMetadata(journeyMap?.metadata), [journeyMap]);

  const backgroundImageUrl = useMemo(() => {
    return journeyMap?.background_image_url || metadata?.background_image_url || '';
  }, [journeyMap, metadata]);

  const canvasWidth = useMemo(() => {
    return toNumber(
      journeyMap?.canvas_width ?? metadata?.canvas_width,
      DEFAULT_CANVAS_WIDTH,
    );
  }, [journeyMap, metadata]);

  const canvasHeight = useMemo(() => {
    return toNumber(
      journeyMap?.canvas_height ?? metadata?.canvas_height,
      DEFAULT_CANVAS_HEIGHT,
    );
  }, [journeyMap, metadata]);

  const sectionDelimiters = useMemo<ViewerSectionDelimiter[]>(() => {
    const raw = journeyMap?.section_delimiters || metadata?.section_delimiters || [];

    return Array.isArray(raw)
      ? raw
        .filter((delimiter: any) => !delimiter?.isDeleted)
        .map(
          (delimiter: any): ViewerSectionDelimiter => ({
            id: delimiter?.id,
            tempId: String(delimiter?.tempId || delimiter?.id || ''),
            x: toNumber(delimiter?.x, 0),
            y: toNumber(delimiter?.y, 0),
            orientation:
              delimiter?.orientation === 'horizontal' ? 'horizontal' : 'vertical',
            style: delimiter?.style || 'solid',
            lineStyle:
              delimiter?.lineStyle === 'dashed' ||
              delimiter?.lineStyle === 'dotted' ||
              delimiter?.lineStyle === 'solid'
                ? delimiter.lineStyle
                : delimiter?.style === 'dashed' ||
                delimiter?.style === 'dotted' ||
                delimiter?.style === 'solid'
                  ? delimiter.style
                  : 'solid',
            color: delimiter?.color || 'neutral',
            label: delimiter?.label || '',
            width: toNumber(delimiter?.width, 2),
            isDeleted: !!delimiter?.isDeleted,
          }),
        )
      : [];
  }, [journeyMap, metadata]);

  const journeyState = useMemo(() => {
    const total = trackableItems.length;
    const completed = trackableItems.filter((item) => viewerCompletionByItemId[String(item.id)]).length;
    const allComplete = total > 0 && completed >= total;

    if (allComplete) {
      return {
        label: 'Complete',
        sentiment: 'success' as const,
        iconAsset: statusOk,
      };
    }

    const hasOverdue = trackableItems.some((item) => {
      const itemId = String(item.id);
      return overdueByItemId[itemId] && !viewerCompletionByItemId[itemId];
    });

    const hasPendingValidation = trackableItems.some((item) => {
      const itemId = String(item.id);
      return !!viewerPendingValidationByItemId[itemId] && !viewerCompletionByItemId[itemId];
    });

    if (isAssigned && hasOverdue) {
      return {
        label: 'Overdue',
        sentiment: 'danger' as const,
        iconAsset: statusNegative,
      };
    }

    if (isAssigned && hasPendingValidation) {
      return {
        label: 'In Progress',
        sentiment: 'in-progress' as const,
        iconAsset: statusInProgress,
      };
    }

    if (isAssigned) {
      return {
        label: 'In Progress',
        sentiment: 'in-progress' as const,
        iconAsset: statusInProgress,
      };
    }

    return {
      label: 'Unassigned',
      sentiment: 'inactive' as const,
      iconAsset: statusGeneralDormant,
    };
  }, [
    isAssigned,
    trackableItems,
    viewerCompletionByItemId,
    viewerPendingValidationByItemId,
    overdueByItemId,
  ]);

  const isItemLocked = (item: any) => !!viewerLockedByItemId[String(item?.id)];
  const isItemCompleted = (item: any) => !!viewerCompletionByItemId[String(item?.id)];

  const handleSelectItem = (item: any) => {
    if (!item?.id) return;

    const itemId = String(item.id);
    const notApplicable = !!viewerNotApplicableByItemId[itemId];

    if (isItemLocked(item) || notApplicable) {
      return;
    }

    if (isAssigned) {
      setSelectedItemId(item.id);
      return;
    }

    setPendingItemId(item.id);
    setAssignmentError(null);
    setAssignmentModalOpen(true);
  };

  const handleConfirmAssignment = async () => {
    try {
      setAssignmentError(null);
      await onRequestAssignment();

      if (pendingItem) {
        const itemId = String(pendingItem.id);
        const locked = !!viewerLockedByItemId[itemId];
        const notApplicable = !!viewerNotApplicableByItemId[itemId];

        if (!locked && !notApplicable) {
          setSelectedItemId(pendingItem.id);
        }
      }

      setPendingItemId(null);
      setAssignmentModalOpen(false);
    } catch (err: any) {
      setAssignmentError(err?.message || 'Unable to create assignment.');
    }
  };

  const handleCancelAssignment = async () => {
    setPendingItemId(null);
    setAssignmentError(null);
    setAssignmentModalOpen(false);
  };

  const handlePreviousItem = (item: any) => {
    if (!item?.id) return;
    setSelectedItemId(item.id);
  };

  const handleNextItem = (item: any) => {
    if (!item?.id) return;
    if (!selectedItem?.id) return;

    const nextItemId = String(item.id);
    const currentIsCompleted = isItemCompleted(selectedItem);
    const nextIsLocked = !!viewerLockedByItemId[nextItemId];
    const nextIsNotApplicable = !!viewerNotApplicableByItemId[nextItemId];

    if (!currentIsCompleted) {
      return;
    }

    if (nextIsLocked || nextIsNotApplicable) {
      return;
    }

    setSelectedItemId(item.id);
  };

  return (
    <div className="hbr-css__layout-col-md">
      <HbrCard
        container
        style={{ '--border-radius': '12px' } as React.CSSProperties}
      >
        <div slot="header" className="hbr-css__layout-col-3xs">
          <div className="hbr-css__layout-row-sm hbr-css__layout-align-center">
            <h2 className="hbr-css__text-heading-lg-bold hbr-css__margin-none">
              {journeyMap?.name || journeyMap?.title || 'Journey Map'}
            </h2>
            <HbrTag size="small">{trackableItems.length} steps</HbrTag>
            <HbrTag size="small" status={true} sentiment={journeyState.sentiment}>
              <HbrIcon slot="prefix" asset={journeyState.iconAsset} />
              {journeyState.label}
            </HbrTag>
          </div>
          {journeyMap?.description ? (
            <div className="hbr-css__text-secondary-sm">{journeyMap.description}</div>
          ) : null}
        </div>

        <div className="hbr-css__layout-col-md">
          <JourneyMapProgress
            items={orderedItems}
            completionByItemId={viewerCompletionByItemId}
            pendingValidationByItemId={viewerPendingValidationByItemId}
            notApplicableByItemId={viewerNotApplicableByItemId}
          />

          <JourneyMapBoard
            items={orderedItems}
            links={links}
            backgroundImageUrl={backgroundImageUrl}
            sectionDelimiters={sectionDelimiters}
            canvasWidth={canvasWidth}
            canvasHeight={canvasHeight}
            selectedItemId={selectedItem?.id ?? null}
            completionByItemId={viewerCompletionByItemId}
            pendingValidationByItemId={viewerPendingValidationByItemId}
            syncStatusByItemId={viewerSyncStatusByItemId}
            notApplicableByItemId={viewerNotApplicableByItemId}
            lockedByItemId={viewerLockedByItemId}
            overdueByItemId={overdueByItemId}
            onSelectItem={handleSelectItem}
          />
        </div>
      </HbrCard>

      <JourneyMapItemModal
        open={!!selectedItem}
        item={selectedItem}
        items={orderedItems}
        assignmentId={assignmentId}
        currentUserId={resolvedCurrentUserId}
        completionByItemId={viewerCompletionByItemId}
        completionReasonByItemId={completionReasonByItemId}
        pendingValidationByItemId={viewerPendingValidationByItemId}
        syncStatusByItemId={viewerSyncStatusByItemId}
        statusReasonByItemId={statusReasonByItemId}
        onClose={() => setSelectedItemId(null)}
        onCompleteItem={onCompleteItem}
        onTaskSubmissionSaved={onTaskSubmissionSaved}
        onPreviousItem={handlePreviousItem}
        onNextItem={handleNextItem}
        onLaunchItem={onLaunchItem}
        onVideoCompleted={onVideoCompleted}
        onLaunchCourseItem={onLaunchCourseItem}
        renderCourseItem={renderCourseItem}
      />

      <StbModal
        open={assignmentModalOpen}
        role="save"
        title={assignmentPromptTitle}
        submitLabel="Confirm"
        cancelLabel="Cancel"
        onClose={handleCancelAssignment}
        onSubmit={handleConfirmAssignment}
        suppressCloseOnSubmit
      >
        <div className="hbr-css__layout-col-sm">
          <div className="hbr-css__text-secondary-sm">{assignmentPromptMessage}</div>

          {pendingItem ? (
            <HbrCard
              style={
                {
                  '--box-shadow': 'none',
                  '--border-width': '2px',
                  '--border-color': 'var(--hbr-color-neutral-border-weak)',
                } as React.CSSProperties
              }
            >
              <div className="hbr-css__layout-col-3xs">
                <div className="hbr-css__text-primary-sm-semibold">
                  {pendingItem?.name || 'Selected step'}
                </div>
                {pendingItem?.description ? (
                  <div className="hbr-css__text-secondary-xs">
                    {pendingItem.description}
                  </div>
                ) : null}
              </div>
            </HbrCard>
          ) : null}

          {assignmentError ? (
            <HbrCard
              sentiment="danger"
              style={
                {
                  '--box-shadow': 'none',
                  '--border-width': '2px',
                  '--border-color': 'var(--hbr-color-danger-border)',
                } as React.CSSProperties
              }
            >
              <div className="hbr-css__text-secondary-sm">{assignmentError}</div>
            </HbrCard>
          ) : null}
        </div>
      </StbModal>
    </div>
  );
};

export default JourneyMapViewer;